<div class="d-flex flex-wrap justify-content-between align-items-center mb-3">
  <div>
    <h5 class="mb-1 fw-bold">Stock Adjustments</h5>
    <span class="sub-text">Manual corrections made outside the purchase order flow</span>
  </div>
</div>

@if (isForbidden) {
  <app-not-authorized
    title="You do not have access to stock adjustments"
    message="Manual stock adjustments are restricted to administrators."></app-not-authorized>
} @else {
  <div class="alert alert-info d-flex align-items-start mb-3" role="alert">
    <i class="bi bi-info-circle-fill me-2 mt-1"></i>
    <div>
      <strong>Adjustments are permanent.</strong>
      Nothing here can be edited or deleted. To undo one, reverse it: that posts the opposite quantity and leaves both
      entries on the record.
    </div>
  </div>

  <!-- Filters -->
  <div class="row g-2 mb-3">
    <div class="col-12 col-md-4 col-lg-3">
      <div class="input-group">
        <span class="input-group-text bg-white border-end-0">
          <i class="bi bi-search text-muted"></i>
        </span>
        <input
          type="text"
          class="form-control border-start-0"
          placeholder="Search material, code or remarks"
          [ngModel]="searchTerm"
          (ngModelChange)="onSearchChange($event)" />
      </div>
    </div>

    <div class="col-12 col-md-4 col-lg-3">
      <ng-select
        placeholder="All Materials"
        [items]="rawMaterials"
        [(ngModel)]="selectedRawMaterial"
        bindValue="id"
        bindLabel="name"
        [clearable]="true"
        [searchable]="true"
        (ngModelChange)="onFilterChange()" />
    </div>

    <div class="col-12 col-md-4 col-lg-2">
      <ng-select
        placeholder="All Warehouses"
        [items]="wareHouses"
        [(ngModel)]="selectedWarehouse"
        bindValue="id"
        bindLabel="name"
        [clearable]="true"
        [searchable]="false"
        (ngModelChange)="onFilterChange()" />
    </div>

    <div class="col-12 col-md-4 col-lg-2">
      <ng-select
        placeholder="All Categories"
        [items]="categories"
        [(ngModel)]="selectedCategory"
        bindValue="id"
        bindLabel="name"
        [clearable]="true"
        [searchable]="false"
        (ngModelChange)="onFilterChange()" />
    </div>

    <div class="col-12 col-md-4 col-lg-2">
      <ng-select
        placeholder="Added or removed"
        [items]="directionOptions"
        [(ngModel)]="selectedDirection"
        bindValue="value"
        bindLabel="label"
        [clearable]="true"
        [searchable]="false"
        (ngModelChange)="onFilterChange()" />
    </div>

    <div class="col-12 col-md-4 col-lg-3">
      <ng-select
        placeholder="All Reasons"
        [items]="reasonOptions"
        [(ngModel)]="selectedReason"
        bindValue="value"
        bindLabel="label"
        [clearable]="true"
        [searchable]="false"
        (ngModelChange)="onFilterChange()" />
    </div>

    <div class="col-12 col-md-4 col-lg-3">
      <ng-select
        placeholder="Adjusted by anyone"
        [items]="employees"
        [(ngModel)]="selectedCreatedBy"
        bindValue="user_id"
        bindLabel="full_name"
        [clearable]="true"
        [searchable]="true"
        (ngModelChange)="onFilterChange()" />
    </div>

    <div class="col-6 col-md-4 col-lg-2">
      <input
        type="date"
        class="form-control"
        aria-label="From date"
        [(ngModel)]="fromDate"
        (ngModelChange)="onFilterChange()" />
    </div>

    <div class="col-6 col-md-4 col-lg-2">
      <input
        type="date"
        class="form-control"
        aria-label="To date"
        [(ngModel)]="toDate"
        (ngModelChange)="onFilterChange()" />
    </div>

    @if (hasActiveFilters) {
      <div class="col-12 col-lg-2 d-flex">
        <button
          class="btn btn-secondary btn-sm w-100 d-flex align-items-center justify-content-center gap-1"
          (click)="clearFilters()">
          <i class="bi bi-x-circle"></i>
          Clear filters
        </button>
      </div>
    }
  </div>

  <app-error-alert [error]="pageError"></app-error-alert>

  @if (isLoading) {
    <app-loading-spinner></app-loading-spinner>
  } @else if (adjustments.length === 0) {
    <div class="empty-state-container">
      <i class="bi bi-sliders"></i>
      <h5>No Stock Adjustments</h5>
      @if (hasActiveFilters) {
        <p class="mb-0">No adjustments match the current search or filters.</p>
      } @else {
        <p class="mb-0">
          Adjustments are made from the
          <strong>Inventory</strong>
          tab, using the Adjust action on a stock row.
        </p>
      }
    </div>
  } @else {
    <div class="table-responsive border bg-white">
      <table class="table align-middle mb-0">
        <thead class="table-light">
          <tr>
            <th>Date</th>
            <th>Material</th>
            <th>Warehouse</th>
            <th class="text-end">Change</th>
            <th class="text-end">Balance After</th>
            <th>Reason</th>
            <th>Remarks</th>
            <th>By</th>
            <th class="text-center">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let adjustment of adjustments">
            <td class="text-nowrap">
              <div>{{ adjustment.created_at | date: 'dd MMM yyyy' }}</div>
              <div class="text-muted small">{{ adjustment.created_at | date: 'shortTime' }}</div>
            </td>
            <td>
              <div class="fw-semibold">{{ adjustment.raw_material_name }}</div>
              <div class="text-muted small font-monospace">{{ adjustment.raw_material_code }}</div>
            </td>
            <td>{{ adjustment.warehouse_name }}</td>
            <td class="text-end text-nowrap">
              <span
                class="fw-bold"
                [class.text-danger]="isRemoval(adjustment)"
                [class.text-success]="!isRemoval(adjustment)">
                {{ signedQuantity(adjustment) }}
              </span>
            </td>
            <td class="text-end text-nowrap">{{ balanceAfter(adjustment) }}</td>
            <td>{{ reasonLabel(adjustment) }}</td>
            <td class="remarks-cell">
              @if (adjustment.remarks) {
                {{ adjustment.remarks }}
              } @else {
                <span class="text-muted">&mdash;</span>
              }
            </td>
            <td class="text-nowrap">{{ adjustment.created_by_name }}</td>
            <td class="text-center">
              <button
                class="btn btn-soft-primary btn-sm text-nowrap"
                (click)="openReverseModal(adjustment)"
                title="Post the opposite adjustment">
                <i class="bi bi-arrow-counterclockwise me-1"></i>
                Reverse
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="mt-4">
      <app-pagination
        [currentPage]="currentPage"
        [totalCount]="totalCount"
        [pageSize]="pageSize"
        [hasNextPage]="hasNextPage"
        [hasPreviousPage]="hasPreviousPage"
        (pageChange)="onPageChange($event)"></app-pagination>
    </div>
  }
}

<!-- Reverse adjustment modal -->
<ng-template #adjustmentModal>
  <div class="modal-header">
    <h5 class="modal-title fw-bold">Reverse Adjustment</h5>
    <button type="button" class="btn-close" aria-label="Close" (click)="closeModal()"></button>
  </div>

  @if (modalContext) {
    <app-stock-adjustment-form
      [context]="modalContext"
      [prefill]="modalPrefill"
      (saved)="onAdjustmentSaved()"
      (cancelled)="closeModal()"></app-stock-adjustment-form>
  }
</ng-template>
